import './TeamDetail.scss'

import {BlockType, Blocks} from '../blocks/Blocks'
import {
	Contentside,
	ContentsideContent,
	ContentsideFixed,
	ContentsideSidebar
} from '../layout/Contentside'
import {FormBlockType, FormSectionType} from '../blocks/FormBlock'
import {Hero, HeroType} from '../layout/Hero'
import {ImageType, PageData, WysiwygType} from '../../types'
import {SidebarBlockType, SidebarBlocks} from '../sidebar/SidebarBlocks'
import {
	TeamDonations,
	TeamDonationsItemType,
	TeamDonationsType
} from './team/TeamDonations'
import {TeamSupport, TeamSupportType} from './team/TeamSupport'

import {Breadcrumbs} from '../layout/Breadcrumbs'
import {CtaRegister} from '../layout/cta/CtaRegister'
import {Infoshare} from '../layout/Infoshare'
import {Layout} from '../layout/Layout'
import {Picture} from '../ui/Picture'
import {Progressbar} from '../ui/Progressbar'
import React from 'react'
import {Share} from '../layout/Share'
import {TeamCollected} from './team/TeamCollected'
import {TeamInviteMember} from './TeamInviteMember'
import {TeamMemberType} from './teams/TeamsSearch'
import {Theme} from '../Theme'
import {Trusted} from '../../util/trusted'
import individualInfoSection from '../evenementen/layout/EditionRegistration/sections/individualInfoSection'
import {style} from '../../util/style'
import teamInfoSection from '../evenementen/layout/EditionRegistration/sections/teamInfoSection'
import {useHistory} from 'react-router'
import {useTranslation} from '../../util/i18n'
import {wrap} from '@codeurs/front'

const styles = style('teamdetail', ['members', 'info', 'thanks'])

type TeamDetailThanksType = {
	title: string
	text: WysiwygType
	buttonText: string
	buttonUrl: string
}

type TeamEditType = {
	type: 'edit'
	reCaptchaKey: string
	editHash: string
	team: {
		value: number
	}
	exportUrl: string
}

type MemberPaidType = TeamEditType & {
	thanks_paid: {
		title: string
		text: WysiwygType
		button?: {text: string; url: string}
	}
}

type TeamThanksType = {
	type: 'thanks'
	thanks: TeamDetailThanksType
}

type TeamInviteType = {
	type: 'invite'
	reCaptchaKey: string
	extra_sections?: Array<FormSectionType>
	inviteHash?: string
	member?: {
		uuid: string
		firstname: string
		lastname: string
		email: string
	}
}

export type TeamDetailType = {
	hero: HeroType
	contentblocks?: Array<BlockType>
	sidebarblocks?: Array<SidebarBlockType>
	blocks?: Array<BlockType>
	team: {
		type: Array<'team' | 'individual'>
		value: number
		total: number
		ogm: string
		city?: string
		description?: WysiwygType
		image?: ImageType
	}
	members: TeamMemberType
	teamSupport: TeamSupportType | null
}

export const TeamDetail: React.FC<
	PageData &
		TeamDetailType &
		(
			| {type: 'detail'; donations: Array<TeamDonationsItemType>}
			| TeamEditType
			| TeamThanksType
			| TeamInviteType
			| MemberPaidType
		)
> = wrap(Layout)(({...props}) => {
	const {
		parents,
		title,
		contentblocks,
		sidebarblocks,
		blocks,
		team,
		hero,
		members,
		member,
		date,
		location,
		registration,
		type,
		thanks,
		reCaptchaKey,
		extra_sections,
		editHash,
		inviteHash,
		teamSupport,
		thanks_paid,
		donations,
		exportUrl
	} = props
	if (type === 'invite')
		return (
			<TeamInviteMember
				hero={hero}
				title={title}
				eventTitle={parents[2].title}
				reCaptchaKey={reCaptchaKey}
				inviteHash={inviteHash}
				extra_sections={extra_sections}
				parents={parents}
				member={member}
				thanks_paid={thanks_paid}
			/>
		)
	return (
		<div className={styles()}>
			<Hero {...hero} markedtitle={title} />
			<Breadcrumbs parents={parents} title={title} />
			<Contentside>
				<ContentsideContent>
					<ContentsideFixed>
						{type === 'thanks' && <TeamDetailThanks {...thanks} />}
						{type === 'edit' && (
							<TeamDetailEdit
								title={title}
								description={team.description}
								type={team.type}
								editHash={editHash}
								reCaptchaKey={reCaptchaKey}
							/>
						)}
						{type === 'detail' && (
							<>
								<Infoshare date={date} location={location} />
								<Progressbar
									value={team?.value}
									total={team?.total}
									className={styles('progressbar')}
								/>
								<TeamDetailMembers
									title={title}
									city={team?.city}
									members={members}
								/>
								<TeamDetailInfo {...team} />
								<CtaRegister
									{...registration}
									className={styles('ctaregister')}
								/>
							</>
						)}
					</ContentsideFixed>
					<Blocks blocks={contentblocks} />
				</ContentsideContent>
				<ContentsideSidebar>
					{type === 'edit' && (
						<SideBarEdit teamcollected={{price: team.value, exportUrl}} />
					)}
					{type === 'detail' && (
						<SideBarDetail
							teamsupport={{...teamSupport}}
							teamdonations={{donations}}
						/>
					)}
					<SidebarBlocks blocks={sidebarblocks} />
				</ContentsideSidebar>
			</Contentside>
			<Blocks blocks={blocks} container={true} />
		</div>
	)
})

const SideBarEdit: React.FC<{
	teamcollected: {price: number; exportUrl: string}
}> = ({teamcollected}) => (
	<ContentsideFixed>
		<TeamCollected className={styles('sidebar-item')} {...teamcollected} />
	</ContentsideFixed>
)

const SideBarDetail: React.FC<{
	teamsupport: TeamSupportType | null
	teamdonations: TeamDonationsType
}> = ({teamsupport, teamdonations}) => (
	<ContentsideFixed>
		{teamsupport !== null && (
			<TeamSupport
				className={styles('sidebar-item')}
				buttonFullwidth={true}
				{...teamsupport}
			/>
		)}
		<TeamDonations className={styles('sidebar-item')} {...teamdonations} />
	</ContentsideFixed>
)

const onSubmitHandler = (response: any) => {
	if (response.path) {
		const history = useHistory()
		history.push(`${response.path}`)
	} else {
		console.error('No path defined', response)
	}
}

const formBlock = (
	title: string,
	description: WysiwygType,
	type: Array<'team' | 'individual'>,
	editHash: string,
	reCaptchaKey: string
): {type: 'form'} & FormBlockType => {
	let fields = []
	if (type[0] === 'individual') {
		fields = {...individualInfoSection}.fields.slice(1)
		fields[0] = {...fields[0], disabled: true} // disable edit because the ath will also change
	} else {
		fields = {...teamInfoSection}.fields.slice(1)
		fields.splice(1, 1) // remove number of team members
		fields[0] = {...fields[0], mod: 'maxwidth', disabled: true} // disable edit because the ath will also change
	}
	return {
		type: 'form' as 'form',
		path: '',
		reCaptchaKey,
		requestUri: window.location.pathname,
		id: 1,
		pageId: 0,
		steps: [
			{
				id: 2,
				parent_id: 1,
				channel: 'step',
				label: null,
				submit_text: 'Opslaan', // todo: translate
				sections: [
					type[0] === 'individual'
						? {
								...individualInfoSection,
								label: null,
								parent_id: 2,
								fields
						  }
						: {
								...teamInfoSection,
								label: null,
								parent_id: 2,
								fields
						  }
				]
			}
		],
		data: {
			'2-1-1': title,
			'2-1-3': description,
			edit: editHash
		},
		useEffect: true,
		onSubmit: onSubmitHandler
	}
}

const TeamDetailEdit: React.FC<{
	title: string
	description: WysiwygType
	type: Array<'team' | 'individual'>
	editHash: string
	reCaptchaKey: string
}> = ({title, description, type, editHash, reCaptchaKey}) => {
	let descr: string = description
		.split('<br />\n')
		.join('\n')
		.split('<br />')
		.join('\n')
		.substr(3)
	descr = descr.substr(0, descr.length - 4)

	return (
		<ContentsideFixed>
			<Blocks
				blocks={[formBlock(title, descr, type, editHash, reCaptchaKey)]}
			/>
		</ContentsideFixed>
	)
}

const TeamDetailThanks: React.FC<TeamDetailThanksType> = ({
	title,
	text,
	buttonText,
	buttonUrl
}) => (
	<div className={styles.thanks()}>
		<Theme.H2 className={styles.thanks('title')} mod="bold">
			{title}
		</Theme.H2>
		<div className={styles.thanks('text')}>
			<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
		</div>
		<div className={styles.thanks('bottom')}>
			<div className={styles.thanks('bottom-item')}>
				<Theme.Button to={buttonUrl} className={styles.thanks('button')}>
					{buttonText}
				</Theme.Button>
			</div>
			<div className={styles.thanks('bottom-item')}>
				<Share text="Deel je actiepagina" />
			</div>
		</div>
	</div>
)

const TeamDetailMembers: React.FC<{
	title: string
	city?: string
	members: Array<TeamMemberType>
}> = ({title, city, members}) => {
	const t = useTranslation('teams').detail
	const membersTitle = city ? title + t.outcity + city : title

	return (
		<div className={styles.members()}>
			<Theme.H3>{membersTitle}</Theme.H3>
			<p className={styles.members('desc')}>{t.members}</p>
			<ul className={styles.members('list')}>
				{members.map((member) => (
					<li key={member.id} className={styles.members('list-member')}>
						<Trusted>{`${member.firstname} ${member.lastname}`}</Trusted>
					</li>
				))}
			</ul>
		</div>
	)
}

const TeamDetailInfo: React.FC<{
	description?: WysiwygType
	image?: ImageType
}> = ({description, image}) => {
	if (!description && !image) return null

	return (
		<div className={styles.info()}>
			{description && (
				<div className={styles.info('desc')}>
					<Theme.Wysiwyg>{description}</Theme.Wysiwyg>
				</div>
			)}
			{image && image.src && (
				<div className={styles.info('image')}>
					<Picture {...image} />
				</div>
			)}
		</div>
	)
}
